// overlayKit/EquationStack.tsx — 3-line vertical stack (line1 / op / line2).
// Migrated from ChocolateFullPreview inline EquationStack. ALWAYS VERTICAL
// (Yash rule). Use this for any "A + B", "A = B", "A → B" relationship.
import React from "react";
import { useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { appear, FONT, TS, win } from "./utils";
import { useColor } from "./palette";

export type EquationStackProps = {
  /** l1/l2 may contain "\n" to break a long line into stacked rows (Yash 2026-07-29). */
  l1: string;
  op: string;
  l2: string;
  l1S: number;
  opS: number;
  l2S: number;
  outS: number;
  /** Palette token for operator (default "secondary"). */
  opColor?: string;
  /** Palette token for BOTH lines (default "neutral"). */
  lineColor?: string;
  /** Per-line overrides. Default to `lineColor`, so omitting them keeps legacy behavior.
   *  House rule (Yash 2026-07-29): the SECOND line is the payoff → give it the accent. */
  l1Color?: string;
  l2Color?: string;
};

export const EquationStack: React.FC<EquationStackProps> = ({
  l1, op, l2, l1S, opS, l2S, outS, opColor = "secondary", lineColor = "neutral",
  l1Color, l2Color,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = frame / fps;
  const wOp = win(t, l1S, outS);
  if (wOp <= 0.001) return null;
  const opHex = useColor(opColor);
  const lineHex = useColor(lineColor);
  const l1Hex = useColor(l1Color ?? lineColor);
  const l2Hex = useColor(l2Color ?? lineColor);

  const lineStyle = (hex: string, atS: number): React.CSSProperties => ({
    color: hex, fontSize: 42, fontWeight: 900, letterSpacing: "0.03em", textShadow: TS,
    opacity: appear(t, atS, 0.16),
    transform: `scale(${interpolate(t, [atS, atS + 0.25], [0.85, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })})`,
    transformOrigin: "left bottom",
  });

  // A "\n" in l1/l2 stacks that line into rows with a tighter gap than the l1/op/l2 gap,
  // so the wrapped rows still read as ONE term.
  const rows = (text: string, hex: string, atS: number) => {
    const parts = text.split("\n");
    if (parts.length === 1) return <div style={lineStyle(hex, atS)}>{text}</div>;
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
        {parts.map((p, i) => (
          <div key={i} style={{ ...lineStyle(hex, atS), lineHeight: 1.06 }}>{p}</div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ position: "absolute", top: 18, left: 22, fontFamily: FONT, opacity: wOp }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
        {rows(l1, l1Hex, l1S)}
        <div style={{ color: opHex, fontSize: 34, fontWeight: 800, textShadow: TS, opacity: appear(t, opS, 0.14) }}>{op}</div>
        {rows(l2, l2Hex, l2S)}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
